import { useTracker } from '@jetshop/core/analytics/Analytics';
import { trackPageEvent } from '@jetshop/core/analytics/tracking';
import React, { useContext, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import useWarningInDev from '../hooks/useWarningInDev';
import ChannelContext from './ChannelContext';
import { useLocation } from 'react-router';
import qs from 'qs';
import { OpenGraphGeneralPageData } from '@jetshop/core/components/OpenGraph';
import { useShopConfig } from '@jetshop/core/hooks/useShopConfig';
import { getLanguageFromCulture } from '../ChannelHandler/channelUtils';
import { SiteLinksSearchStructuredData } from './StructuredData';
function renderAlternateRoutes(alternateRoute, channelsById, selectedChannelGroup = undefined) {
    const channel = channelsById[alternateRoute.channelId];
    // Only include alternate routes for this particular group if available
    if (selectedChannelGroup && !selectedChannelGroup.includes(channel.id)) {
        return null;
    }
    const url = channel.url;
    const route = alternateRoute.route;
    let languageBasename = '';
    if (channel.defaultLanguage.culture !== alternateRoute.culture) {
        languageBasename = `/${getLanguageFromCulture(alternateRoute.culture)}`;
    }
    const href = `${url}${languageBasename}${route}`;
    return (React.createElement("link", Object.assign({ key: href + alternateRoute.culture, rel: "alternate", href: href, hrefLang: alternateRoute.alias || alternateRoute.culture }, (channel.groupName && { 'data-group': channel.groupName }))));
}
const queryParamsWhitelist = ['page'];
const Head = ({ data, route }) => {
    var _a, _b;
    const { search, pathname } = useLocation();
    const queryStringObject = qs.parse(search, { ignoreQueryPrefix: true });
    Object.keys(queryStringObject).forEach((key) => {
        !queryParamsWhitelist.includes(key) && delete queryStringObject[key];
    });
    const whiteListedSearch = qs.stringify(queryStringObject, {
        addQueryPrefix: true
    });
    const { selectedChannel, channelsById, selectedChannelGroup } = useContext(ChannelContext);
    const lang = selectedChannel.language.culture.split('-')[0];
    const metaTags = ((_a = data === null || data === void 0 ? void 0 : data.head) === null || _a === void 0 ? void 0 : _a.metaTags) || [];
    let languageBasename = '';
    if (!selectedChannel.language.isDefault) {
        languageBasename = `/${getLanguageFromCulture(selectedChannel.language.culture)}`;
    }
    const hasIndexingOptions = (data.__typename === 'Category' || data.__typename === 'Page') &&
        data.allowWebIndexing !== undefined;
    const disallowWebIndexing = (_b = (hasIndexingOptions && !data.allowWebIndexing)) !== null && _b !== void 0 ? _b : false;
    const fullPath = `${languageBasename}${(route === null || route === void 0 ? void 0 : route.canonicalPath) || ''}`;
    const href = (route === null || route === void 0 ? void 0 : route.canonicalPath) &&
        `${selectedChannel.url}${fullPath}${whiteListedSearch}`;
    const isCurrentRouteCanonical = pathname === (route === null || route === void 0 ? void 0 : route.canonicalPath);
    useWarningInDev(route && !route.alternateRoutes, `You must include the RouteMetaFragment in your RouteQuery.gql
    Please see https://gitlab.jetshop.se/jetshop/flight/blob/master/packages/template-trend/src/components/RouteQuery.gql for an example
    `);
    const { openGraph } = useShopConfig();
    const disableOpenGraphGeneralData = openGraph
        ? !!openGraph.disableDefaultGeneralData
        : false;
    const track = useTracker();
    useEffect(() => {
        var _a;
        track(trackPageEvent({
            pathname: window.location.pathname,
            title: (_a = data === null || data === void 0 ? void 0 : data.head) === null || _a === void 0 ? void 0 : _a.title
        }));
    }, [data, track]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Helmet, null,
            React.createElement("html", { lang: lang }),
            (data === null || data === void 0 ? void 0 : data.head) && React.createElement("title", null, data.head.title),
            metaTags.map((tag) => (React.createElement("meta", { name: tag.name, content: tag.content, key: tag.name }))),
            route &&
                isCurrentRouteCanonical &&
                route.alternateRoutes &&
                route.alternateRoutes.map((alternateRoute) => renderAlternateRoutes(alternateRoute, channelsById, selectedChannelGroup &&
                    selectedChannelGroup.map((channel) => channel.id))),
            href && React.createElement("link", { rel: "canonical", href: href }),
            disallowWebIndexing && React.createElement("meta", { name: "robots", content: "noindex" })),
        !disableOpenGraphGeneralData && (React.createElement(OpenGraphGeneralPageData, { route: route, data: data })),
        (route === null || route === void 0 ? void 0 : route.canonicalPath) === '/' && React.createElement(SiteLinksSearchStructuredData, null)));
};
export default Head;
//# sourceMappingURL=Head.js.map